// asset-catalog-ui/src/views/Admin/RoiInsights/RoiInsights.tsx

import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from 'store/rootReducer';
import { showToastNotification } from 'store/ui/toasts/toastsActions';
import { ToastType } from 'store/ui/toasts/toastsTypes';
import SimpleLoader from 'components/loaders/SimpleLoader';
import { extractErrorMessage } from 'views/Admin/_shared/errorMessage';
import { UsageWorkspace } from 'views/Admin/UsageReport/types';
import RoiInsightsFilters from './RoiInsightsFilters';
import CostAvoidanceSection from './sections/CostAvoidanceSection';
import StatisticsSection from './sections/StatisticsSection';
import { useCalculatorSettings } from './hooks/useCalculatorSettings';
import { useRoiInsights } from './hooks/useRoiInsights';
import { exportRoiInsightsPdf } from './pdf/exportRoiInsightsPdf';
import { DEFAULT_ROI_FILTERS, RoiFilters } from './types';
import './RoiInsights.scss';

const RoiInsights: React.FC = () => {
  const { t } = useTranslation('admin');
  const dispatch = useDispatch();
  const [filters, setFilters] = useState<RoiFilters>(DEFAULT_ROI_FILTERS);
  const [settings, setSettings] = useCalculatorSettings();
  const { data, isLoading, error } = useRoiInsights(filters, settings);

  const workspaces = useSelector(
    (state: ApplicationState) => state.blazeUserConfig.blazeUserConfig?.user?.workspaces ?? [],
  );
  const usageWorkspaces: UsageWorkspace[] = useMemo(() => workspaces.map(w => ({ id: w.id, name: w.name })), [
    workspaces,
  ]);

  useEffect(() => {
    if (error) {
      dispatch(showToastNotification(ToastType.Danger, t('roiInsights.errors.loadFailed'), extractErrorMessage(error)));
    }
  }, [error, dispatch, t]);

  const handleDownloadPdf = () => {
    if (!data) return;
    try {
      exportRoiInsightsPdf(data, settings, filters);
    } catch (e) {
      dispatch(showToastNotification(ToastType.Danger, t('roiInsights.errors.exportFailed'), extractErrorMessage(e)));
    }
  };

  return (
    <section className="admin-page roi-insights-page">
      <RoiInsightsFilters
        filters={filters}
        workspaces={usageWorkspaces}
        onChange={setFilters}
        onDownloadPdf={handleDownloadPdf}
      />

      <hr className="roi-divider" />

      {isLoading && !data ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <SimpleLoader />
        </div>
      ) : data ? (
        <>
          <CostAvoidanceSection settings={settings} onSettingsChange={setSettings} summary={data.summary} />
          <StatisticsSection data={data} />
        </>
      ) : null}
    </section>
  );
};

export default RoiInsights;
